import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Project } from '../types/project';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/** Which banner the prompt is sitting in — drives only the wrapper spacing/border.
 *  One home today: the Get data page. */
export type UtilityRequestVariant = 'ami-data';

/**
 * Amber says no data route is known; blue says one is. The Get data page decides,
 * because the prompt itself no longer means "we cannot serve this utility" — it
 * asks the contractor to tell us they work in the territory either way.
 */
export type UtilityRequestTone = 'amber' | 'blue';

interface UtilityRequestPromptProps {
  project: Partial<Project>;
  variant: UtilityRequestVariant;
  tone?: UtilityRequestTone;
  /** The banner's copy (today, the Get data page's note). Rendered in every state. */
  children?: ReactNode;
}

interface ExistingRequest {
  id: string;
  utility_name: string;
}

const WRAPPER_SPACING: Record<UtilityRequestVariant, string> = {
  'ami-data': 'mb-4',
};

const TONE_CLASSES: Record<UtilityRequestTone, { box: string; button: string; label: string; link: string }> = {
  amber: {
    box: 'bg-amber-50 border-amber-300 text-amber-800',
    button: 'border-amber-400 bg-white text-amber-900 hover:bg-amber-100',
    label: 'text-amber-900',
    link: 'text-amber-900 hover:text-amber-950',
  },
  blue: {
    box: 'bg-blue-50 border-blue-200 text-blue-900',
    button: 'border-blue-300 bg-white text-blue-900 hover:bg-blue-100',
    label: 'text-blue-900',
    link: 'text-blue-900 hover:text-blue-950',
  },
};

// Locally-created projects use a `local_<n>` id that is not a DB row yet, so it
// cannot satisfy the project_id foreign key.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pause in typing before the "already requested?" lookup re-runs. */
const RECHECK_DEBOUNCE_MS = 400;

const INPUT_CLASS =
  'w-full max-w-sm px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';

function trimmedOrNull(value: string | undefined | null): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function UtilityRequestPrompt({ project, variant, tone = 'amber', children }: UtilityRequestPromptProps) {
  const { user } = useAuth();
  const classes = TONE_CLASSES[tone];

  // A project with no utility_id is on the "Other (not listed)" path — either
  // nothing is selected yet, or the contractor picked the catch-all option.
  const isUnlisted = !project.utility_id;
  const projectUtilityName = (project.ami_data_provider ?? '').trim();

  const [expanded, setExpanded] = useState(false);
  // Latches true once the first existence check resolves, and never goes back.
  // Re-checks caused by the contractor typing a utility name must not tear the
  // banner's contents down and rebuild them on every character.
  const [ready, setReady] = useState(false);
  const [submittedName, setSubmittedName] = useState<string | null>(null);

  const [typedName, setTypedName] = useState('');
  const [annualJobs, setAnnualJobs] = useState('');
  const [hasContact, setHasContact] = useState(false);
  const [contactNote, setContactNote] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The name we will actually record: the typed one on the unlisted path,
  // otherwise the utility already selected on the project.
  const effectiveName = (isUnlisted ? typedName : projectUtilityName).trim();

  const hasCheckedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;

    if (!userId || !projectUtilityName) {
      setSubmittedName(null);
      setReady(true);
      hasCheckedRef.current = true;
      return;
    }

    async function checkExisting() {
      // Fetch this user's own requests (RLS scopes the read) and match in JS —
      // utility names contain commas and periods that would need escaping in a
      // PostgREST `ilike` filter.
      const { data, error: fetchError } = await supabase
        .from('utility_requests')
        .select('id, utility_name')
        .eq('user_id', userId);
      if (cancelled) return;
      if (fetchError) {
        // A failed lookup must never hide the request affordance.
        console.error('Error checking utility requests:', fetchError);
        setSubmittedName(null);
      } else {
        const match = (data as ExistingRequest[] | null)?.find(
          (r) => r.utility_name.trim().toLowerCase() === projectUtilityName.toLowerCase()
        );
        setSubmittedName(match ? match.utility_name : null);
      }
      setReady(true);
      hasCheckedRef.current = true;
    }

    // On the unlisted path the name arrives one keystroke at a time from the
    // Service Info field. Once the first check is behind us, wait for a pause
    // in typing so a name costs one lookup instead of one per character.
    const timer = window.setTimeout(checkExisting, hasCheckedRef.current ? RECHECK_DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user, projectUtilityName]);

  // Prefill the unlisted name field from whatever the contractor already typed
  // into the Service Info "Utility Name" box.
  useEffect(() => {
    if (isUnlisted && projectUtilityName) setTypedName(projectUtilityName);
  }, [isUnlisted, projectUtilityName]);

  function resetForm() {
    setExpanded(false);
    setError(null);
    setAnnualJobs('');
    setHasContact(false);
    setContactNote('');
  }

  async function handleSubmit() {
    if (!user) {
      setError('You must be signed in to request utility support.');
      return;
    }
    const name = effectiveName;
    if (!name) {
      setError('Enter the utility name.');
      return;
    }

    let jobs: number | null = null;
    if (annualJobs.trim() !== '') {
      const parsed = Number.parseInt(annualJobs, 10);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError('Enter a whole number of jobs, or leave it blank.');
        return;
      }
      jobs = parsed;
    }

    setSaving(true);
    setError(null);

    const payload = {
      user_id: user.id,
      project_id: project.id && UUID_PATTERN.test(project.id) ? project.id : null,
      utility_id: isUnlisted ? null : project.utility_id ?? null,
      utility_name: name,
      is_unlisted: isUnlisted,
      service_city: trimmedOrNull(project.customer_city),
      service_state: trimmedOrNull(project.customer_state),
      service_zip: trimmedOrNull(project.customer_zip),
      annual_jobs: jobs,
      has_contact: hasContact,
      contact_note: hasContact ? trimmedOrNull(contactNote) : null,
    };

    try {
      // Upsert against the (user_id, lower(utility_name)) unique index. That
      // index is on an expression, which PostgREST cannot target with
      // on_conflict, so resolve the existing row first and update it.
      const existingId = await findExistingId(user.id, name);
      const { error: writeError } = existingId
        ? await supabase.from('utility_requests').update(payload).eq('id', existingId)
        : await supabase.from('utility_requests').insert(payload);

      if (writeError) {
        // Lost a race against a concurrent insert of the same utility — the
        // unique index fired, so fall back to updating the row that won.
        if (writeError.code === '23505') {
          const raceId = await findExistingId(user.id, name);
          if (raceId) {
            const { error: retryError } = await supabase
              .from('utility_requests')
              .update(payload)
              .eq('id', raceId);
            if (retryError) throw retryError;
            setSubmittedName(name);
            return;
          }
        }
        throw writeError;
      }

      setSubmittedName(name);
    } catch (e) {
      console.error('Error saving utility request:', e);
      const message = e instanceof Error ? e.message : String(e);
      setError(`Could not send your request: ${message}. Please try again.`);
    } finally {
      setSaving(false);
    }
  }

  async function findExistingId(userId: string, name: string): Promise<string | null> {
    const { data, error: fetchError } = await supabase
      .from('utility_requests')
      .select('id, utility_name')
      .eq('user_id', userId);
    if (fetchError) throw fetchError;
    const match = (data as ExistingRequest[] | null)?.find(
      (r) => r.utility_name.trim().toLowerCase() === name.toLowerCase()
    );
    return match?.id ?? null;
  }

  // STATE C — already logged, this session or a previous one. The routes stay
  // on screen: logging demand never hid a way to get the data, and it must not
  // start to now that the copy above the prompt is the picker itself.
  if (submittedName) {
    return (
      <div className={`${WRAPPER_SPACING[variant]} rounded-md border ${classes.box} px-4 py-3 text-sm`}>
        {children}
        <div
          className="mt-3 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800"
          data-testid="utility-request-confirmation"
        >
          Logged. We'll email you when TrueLoad has a direct connection with {submittedName}.
        </div>
      </div>
    );
  }

  return (
    <div className={`${WRAPPER_SPACING[variant]} rounded-md border ${classes.box} px-4 py-3 text-sm`}>
      {children}

      {ready && !expanded && (
        // STATE A — collapsed.
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            data-testid="utility-request-open"
            className={`px-3 py-1.5 rounded-md border ${classes.button} text-sm font-medium transition-colors touch-manipulation`}
          >
            Ask TrueLoad to connect with this utility
          </button>
        </div>
      )}

      {ready && expanded && (
        // STATE B — expanded form.
        <div className="mt-4 space-y-3" data-testid="utility-request-form">
          <p className="text-sm">
            Tell us you work in this territory. It decides which utilities TrueLoad connects
            to next, and it never holds up this project.
          </p>
          {isUnlisted && (
            <div>
              <label className={`block text-sm font-medium ${classes.label} mb-1`}>
                Which utility? <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Utility name"
                disabled={saving}
                data-testid="utility-request-name"
                className={INPUT_CLASS}
              />
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium ${classes.label} mb-1`}>
              Roughly how many jobs a year in this territory?{' '}
              <span className="font-normal opacity-80">(optional)</span>
            </label>
            <input
              type="number"
              min={0}
              step={1}
              value={annualJobs}
              onChange={(e) => setAnnualJobs(e.target.value)}
              disabled={saving}
              data-testid="utility-request-annual-jobs"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label className={`flex items-center gap-2 text-sm ${classes.label}`}>
              <input
                type="checkbox"
                checked={hasContact}
                onChange={(e) => setHasContact(e.target.checked)}
                disabled={saving}
                data-testid="utility-request-has-contact"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              I have a contact at this utility
            </label>
          </div>

          {hasContact && (
            <div>
              <label className={`block text-sm font-medium ${classes.label} mb-1`}>
                Who? (name or role){' '}
                <span className="font-normal opacity-80">(optional)</span>
              </label>
              <input
                type="text"
                value={contactNote}
                onChange={(e) => setContactNote(e.target.value)}
                disabled={saving}
                data-testid="utility-request-contact-note"
                className={INPUT_CLASS}
              />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-700" data-testid="utility-request-error">
              {error}
            </p>
          )}

          <div className="flex items-center gap-4 pt-1">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || !effectiveName}
              data-testid="utility-request-send"
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors touch-manipulation disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
            >
              {saving ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              disabled={saving}
              data-testid="utility-request-cancel"
              className={`text-sm underline ${classes.link} disabled:opacity-50`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
